import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface WizardStepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  onStepClick?: (step: number) => void;
}

export default function WizardStepIndicator({
  currentStep,
  totalSteps,
  onStepClick,
}: WizardStepIndicatorProps) {
  const { t } = useTranslation();

  const allSteps = [
    { number: 1, label: t('wizard.basicsStep') },
    { number: 2, label: t('wizard.overviewStep') },
    { number: 3, label: t('wizard.voiceStep') },
    { number: 4, label: t('wizard.chaptersStep') },
    { number: 5, label: t('wizard.writeStep') },
  ];

  const steps = allSteps.slice(0, totalSteps);
  const currentStepLabel = steps.find((s) => s.number === currentStep)?.label || '';

  const handleStepClick = (stepNumber: number) => {
    if (onStepClick && stepNumber < currentStep) {
      onStepClick(stepNumber);
    }
  };

  return (
    <div className="mb-8 w-full">
      {/* Mobile: Compact dots with progress bar */}
      <div className="sm:hidden">
        <div className="flex items-center justify-center gap-2 mb-2">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <motion.button
                type="button"
                onClick={() => handleStepClick(step.number)}
                disabled={step.number >= currentStep}
                initial={false}
                animate={{
                  scale: currentStep === step.number ? 1.2 : 1,
                }}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep > step.number
                    ? 'bg-primary-500'
                    : currentStep === step.number
                      ? 'bg-primary-500 ring-2 ring-primary-300 dark:ring-primary-700'
                      : 'bg-gray-300 dark:bg-gray-600'
                } ${step.number < currentStep ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
              />
              {index < steps.length - 1 && (
                <div
                  className={`w-4 h-0.5 mx-1 rounded ${
                    currentStep > step.number ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="text-center">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {currentStep}/{totalSteps}
          </span>
          <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
            {currentStepLabel}
          </span>
        </div>
      </div>

      {/* Desktop: Dots with labels */}
      <div className="hidden sm:flex flex-col items-center w-full">
        <div className="flex items-center justify-center gap-1">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <motion.button
                type="button"
                onClick={() => handleStepClick(step.number)}
                disabled={step.number >= currentStep}
                initial={false}
                animate={{
                  scale: currentStep === step.number ? 1.15 : 1,
                }}
                className={`w-4 h-4 rounded-full transition-all flex items-center justify-center ${
                  currentStep > step.number
                    ? 'bg-primary-500'
                    : currentStep === step.number
                      ? 'bg-primary-500 ring-[3px] ring-primary-200 dark:ring-primary-800'
                      : 'bg-gray-300 dark:bg-gray-600'
                } ${step.number < currentStep ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
              >
                {currentStep > step.number && (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </motion.button>
              {index < steps.length - 1 && (
                <div
                  className={`w-8 lg:w-12 h-0.5 mx-1.5 rounded ${
                    currentStep > step.number ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-1 mt-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {currentStep}/{totalSteps}
          </span>
          <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
            {currentStepLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
